import { getLevelColors, getCurrentUserLevel } from './levelColors';

/**
 * Levels that should have custom theme colors
 * All other levels will use the default green theme
 */
const CUSTOM_THEME_LEVELS = [
    'dealer',
    'distributor',
    'wholesaler',
    'mega wholesaler'
];

/**
 * Check if a level should have custom theme
 * @param {string} level - User's current level
 * @returns {boolean} True if level should have custom theme
 */
const shouldApplyCustomTheme = (level) => {
    if (!level) return false;
    const normalizedLevel = level.toLowerCase().trim();
    return CUSTOM_THEME_LEVELS.includes(normalizedLevel);
};

/**
 * Apply level-based colors to CSS variables
 * This updates the :root CSS variables based on user's current level
 * ONLY applies custom colors for: Dealer, Distributor, Wholesaler, Mega Wholesaler
 * All other users keep the default green theme
 * USES DARK COLOR VARIANTS for professional appearance
 */
export const applyLevelTheme = () => {
    try {
        const userLevel = getCurrentUserLevel();

        // Check if this level should have custom theme
        if (!shouldApplyCustomTheme(userLevel)) {
            console.log(`✅ Keeping default green theme for level: ${userLevel}`);
            resetToDefaultTheme();
            return {
                level: userLevel,
                theme: 'default',
                message: 'Using default green theme'
            };
        }

        // Apply custom theme for specific levels
        const colors = getLevelColors(userLevel);

        // Get the root element
        const root = document.documentElement;

        // Update CSS variables with level-specific DARK colors
        // Using dark variants for a more professional, darker look

        // Primary colors - ALL using DARK shades
        root.style.setProperty('--mobile-bg-primary', colors.dark);
        root.style.setProperty('--mobile-bg-primary-card', colors.dark);
        root.style.setProperty('--mobile-bg-primary-dark', colors.dark);

        // Accent and success colors - using DARK shades
        root.style.setProperty('--mobile-bg-accent', colors.dark);
        root.style.setProperty('--mobile-primary', colors.dark);
        root.style.setProperty('--mobile-primary-dark', colors.dark);
        root.style.setProperty('--mobile-success', colors.dark);

        // Button colors - using DARK shades
        root.style.setProperty('--mobile-btn-primary', colors.dark);
        root.style.setProperty('--mobile-btn-primary-hover', colors.dark);
        root.style.setProperty('--mobile-btn-primary-dark', colors.dark);

        // Border colors
        root.style.setProperty('--mobile-border-light', colors.light);
        root.style.setProperty('--mobile-border-medium', colors.dark);

        console.log(`✅ Custom DARK theme applied for level: ${userLevel}`, colors);

        return {
            level: userLevel,
            theme: 'custom-dark',
            colors: colors
        };
    } catch (error) {
        console.error('Error applying level theme:', error);
        return null;
    }
};

/**
 * Reset theme to default green colors
 */
export const resetToDefaultTheme = () => {
    const root = document.documentElement;

    // Default green theme (your original colors)
    const defaultColors = {
        primary: '#046d31',
        primaryCard: '#095A2D',
        primaryDark: '#031e0f',
        bgDark: '#011409',
        accent: '#8CC63E',
        borderLight: '#A3C1A3',
        borderMedium: '#4E6E58',
        btnPrimary: '#2563EB',
        btnPrimaryHover: '#1D4ED8',
        btnPrimaryDark: '#1E3A8A'
    };

    root.style.setProperty('--mobile-bg-primary', defaultColors.primary);
    root.style.setProperty('--mobile-bg-primary-card', defaultColors.primaryCard);
    root.style.setProperty('--mobile-bg-primary-dark', defaultColors.primaryDark);
    root.style.setProperty('--mobile-bg-dark', defaultColors.bgDark);
    root.style.setProperty('--mobile-bg-accent', defaultColors.accent);
    root.style.setProperty('--mobile-primary', '#0E7B3E');
    root.style.setProperty('--mobile-primary-dark', defaultColors.primaryCard);
    root.style.setProperty('--mobile-success', defaultColors.accent);
    root.style.setProperty('--mobile-btn-primary', defaultColors.btnPrimary);
    root.style.setProperty('--mobile-btn-primary-hover', defaultColors.btnPrimaryHover);
    root.style.setProperty('--mobile-btn-primary-dark', defaultColors.btnPrimaryDark);
    root.style.setProperty('--mobile-border-light', defaultColors.borderLight);
    root.style.setProperty('--mobile-border-medium', defaultColors.borderMedium);

    console.log('✅ Reset to default green theme');
};

/**
 * Initialize theme on app load
 * Call this in your App.js or index.js
 */
export const initializeLevelTheme = () => {
    // Check if user is logged in
    const user = localStorage.getItem('htamsUser');

    if (user) {
        // Apply level-based theme (will check if level qualifies for custom theme)
        applyLevelTheme();

        // Listen for storage changes (when user logs in/out in another tab)
        window.addEventListener('storage', (e) => {
            if (e.key === 'htamsUser') {
                if (e.newValue) {
                    applyLevelTheme();
                } else {
                    resetToDefaultTheme();
                }
            }
        });
    } else {
        // Use default theme if not logged in
        resetToDefaultTheme();
    }
};

/**
 * Get current theme colors
 */
export const getCurrentThemeColors = () => {
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);

    return {
        bgPrimary: computedStyle.getPropertyValue('--mobile-bg-primary').trim(),
        bgPrimaryCard: computedStyle.getPropertyValue('--mobile-bg-primary-card').trim(),
        bgAccent: computedStyle.getPropertyValue('--mobile-bg-accent').trim(),
        primary: computedStyle.getPropertyValue('--mobile-primary').trim(),
        primaryDark: computedStyle.getPropertyValue('--mobile-primary-dark').trim(),
        btnPrimary: computedStyle.getPropertyValue('--mobile-btn-primary').trim()
    };
};
